import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import useClientMultiTeam from '~/hooks/useClientMultiTeamId';
import { useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';

interface UseSelectedTeamId {
  showTeamSelect: boolean;
  initialTeamId: number | null;
  selectedActTeam: number | null;
  setSelectedActTeam: Dispatch<SetStateAction<number | null>>;
}

const useSelectedTeamId = (teamIdQuery: string, storageKey: string): UseSelectedTeamId => {
  const { current: client, defaultClient } = useStoreState(state => state.client);
  const { isLocalAdmin, isProgramAssistant, team, isGlobalUser } = useRole();

  const showTeamSelect = isLocalAdmin || isProgramAssistant;

  const { getClientMultiTeamId } = useClientMultiTeam();
  const clientTeamId = getClientMultiTeamId(client?.actTeams ?? defaultClient?.actTeams);

  const initialTeamId =
    showTeamSelect || isGlobalUser ? clientTeamId || Number(teamIdQuery) : team?.id;

  const saveSessionStorageTeamId = sessionStorage.getItem(storageKey);

  const [selectedActTeam, setSelectedActTeam] = useState<number | null>(
    Number(teamIdQuery) || Number(saveSessionStorageTeamId) || initialTeamId,
  );

  useEffect(() => {
    if (!selectedActTeam && !saveSessionStorageTeamId && initialTeamId) {
      setSelectedActTeam(initialTeamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActTeam, saveSessionStorageTeamId, initialTeamId]);

  useEffect(() => {
    if (selectedActTeam && showTeamSelect) {
      if (saveSessionStorageTeamId && selectedActTeam === Number(saveSessionStorageTeamId)) {
        return;
      }

      sessionStorage.setItem(storageKey, selectedActTeam.toString());
    }
  }, [selectedActTeam, showTeamSelect, saveSessionStorageTeamId, storageKey]);

  return {
    showTeamSelect,
    initialTeamId,
    selectedActTeam,
    setSelectedActTeam,
  };
};

export default useSelectedTeamId;
